import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import type { CommentAttachment } from '@tourfold/libraries/tf-api-client-public-v2';
import { FormatBytesPipe } from '../pipes/format-bytes.pipe';

/**
 * Maps an attachment's MIME type to the icon class shown on the chip. The chip stays compact —
 * we don't try to be exhaustive (the long tail falls through to the generic file icon), just
 * the handful of buckets users actually distinguish at a glance.
 */
function iconForMime(mime: string): string {
  if (mime.startsWith('image/')) return 'pi pi-image';
  if (mime === 'application/pdf') return 'pi pi-file-pdf';
  if (mime.includes('word') || mime.includes('msword')) return 'pi pi-file-word';
  if (mime.includes('excel') || mime.includes('sheet')) return 'pi pi-file-excel';
  return 'pi pi-file';
}

/**
 * One non-inline attachment rendered as a clickable chip below the comment body. Inline images
 * (referenced from the markdown via `attachment:<uuid>`) are rendered in the body itself; this
 * component is only used for the residual chip-row population.
 *
 * Click behaviour is left to the host (`<tf-attachments-row>` → `<tf-comments>`) — the chip
 * emits {@link attachmentClick} and the host decides whether to open the preview modal or
 * trigger a direct download based on `is_previewable`. Keeping that logic at the row level
 * means the chip stays presentational and doesn't pull in the preview-dialog dependency.
 *
 * Deleted attachments (`is_deleted=true`) render in a muted state with no click affordance —
 * the underlying blob is gone, so there's nothing to open.
 */
@Component({
  selector: 'tf-attachment-chip',
  standalone: true,
  imports: [TranslateModule, FormatBytesPipe],
  templateUrl: './attachment-chip.component.html',
  styleUrl: './attachment-chip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentChipComponent {
  readonly attachment = input.required<CommentAttachment>();
  readonly attachmentClick = output<CommentAttachment>();

  protected readonly iconClass = computed(() => iconForMime(this.attachment().mimeType));

  protected onClick(): void {
    if (this.attachment().isDeleted) return;
    this.attachmentClick.emit(this.attachment());
  }
}
